import React, { useState, useEffect } from "react";
import { Collapse, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { useOcProperty, useOcCustomerLicences, useOcSnackbar } from "oc/hooks";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcSettingSwitch,
  OcSettingsDivider,
} from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";

type InitialState = {
  change: boolean;
  approval: boolean;
  working: boolean;
  showBalanceFrame: boolean;
  approveAll: boolean;
  examinerApproval: boolean;
};

type MyTimeGeneralSettingsCardProps = {
  isMobile: boolean;
};

export const MyTimeGeneralSettingsCard: React.FC<MyTimeGeneralSettingsCardProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();
  const { licences } = useOcCustomerLicences();
  const enabledRequests = licences?.myTimeEmployeeRequests || false;
  const { setOpenMessage } = useOcSnackbar();

  const [changeEnabled, setChangeEnabled] = useState(false);
  const [approvalEnabled, setApprovalEnabled] = useState(false);
  const [workingEnabled, setWorkingEnabled] = useState(false);
  const [showBalanceFrameEnabled, setShowBalanceFrameEnabled] = useState(true);
  const [approveAllEnabled, setApproveAllEnabled] = useState(false);
  const [examinerApprovalEnabled, setExaminerApprovalEnabled] = useState(false);

  const [initial, setInitial] = useState<InitialState>({
    change: false,
    approval: false,
    working: false,
    showBalanceFrame: true,
    approveAll: false,
    examinerApproval: false,
  });
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    data: changeData,
    loading: loadingChange,
  } = useOcProperty({
    resource: "myTime",
    key: "changeMyTimeEnabled",
  });

  const {
    data: approvalData,
    loading: loadingApproval,
  } = useOcProperty({
    resource: "myTime",
    key: "userApprovalEnabled",
  });

  const {
    data: workingData,
    loading: loadingWorking,
  } = useOcProperty({
    resource: "myTime",
    key: "changeWorkingTimeEnabled",
  });

  const {
    data: approveData,
    loading: loadingApprove,
  } = useOcProperty({
    resource: "myTime",
    key: "approveAllRequestsEnabled",
  });

  const {
    data: examinerApprovalData,
    loading: loadingExaminerApproval,
  } = useOcProperty({
    resource: "myTime",
    key: "examinerApprovalEnabled",
  });

  const {
    data: showBalanceFrameData,
    loading: loadingShowBalanceFrame,
  } = useOcProperty({
    resource: "myTime",
    key: "showBalanceFrameEnabled",
  });

  useEffect(() => {
    if (!loadingChange) setChangeEnabled(changeData === "true");
  }, [changeData, loadingChange]);

  useEffect(() => {
    if (!loadingApproval) setApprovalEnabled(approvalData === "true");
  }, [approvalData, loadingApproval]);

  useEffect(() => {
    if (!loadingWorking) setWorkingEnabled(workingData === "true");
  }, [workingData, loadingWorking]);

  useEffect(() => {
    if (!loadingApprove) setApproveAllEnabled(approveData === "true");
  }, [approveData, loadingApprove]);

  useEffect(() => {
    if (!loadingExaminerApproval) setExaminerApprovalEnabled(examinerApprovalData === "true");
  }, [examinerApprovalData, loadingExaminerApproval]);

  useEffect(() => {
    if (!loadingShowBalanceFrame) setShowBalanceFrameEnabled(showBalanceFrameData === "true");
  }, [showBalanceFrameData, loadingShowBalanceFrame]);

  useEffect(() => {
    if (!loadingChange && !loadingApproval && !loadingWorking && !loadingApprove && !loadingExaminerApproval && !loadingShowBalanceFrame) {
      setInitial({
        change: changeData === "true",
        approval: approvalData === "true",
        working: workingData === "true",
        showBalanceFrame: showBalanceFrameData === "true",
        approveAll: approveData === "true",
        examinerApproval: examinerApprovalData === "true",
      });
    }
  }, [
    changeData,
    approvalData,
    workingData,
    showBalanceFrameData,
    approveData,
    examinerApprovalData,
    loadingChange,
    loadingApproval,
    loadingWorking,
    loadingShowBalanceFrame,
    loadingApprove,
    loadingExaminerApproval,
  ]);

  // Mutation for saving changes
  const [updateProperty, { loading: saving }] = useMutation(OC_UPDATE_PROPERTY, {
    onError: () =>
      setOpenMessage({ type: "error", message: t("OC_PROPERTY_SAVE_FAILED") }),
    onCompleted: () =>
      setOpenMessage({ type: "success", message: t("OC_PROPERTY_SAVE_SUCCEEDED") }),
  });

  const handleSave = () => {
    let examinerApprovalToSave = examinerApprovalEnabled;
    if (!approvalEnabled && examinerApprovalEnabled) {
      examinerApprovalToSave = false;
      setExaminerApprovalEnabled(false);
    }

    if (changeEnabled !== initial.change)
      updateProperty({ variables: { resource: "myTime", key: "changeMyTimeEnabled", value: String(changeEnabled) } });
    if (approvalEnabled !== initial.approval)
      updateProperty({ variables: { resource: "myTime", key: "userApprovalEnabled", value: String(approvalEnabled) } });
    if (workingEnabled !== initial.working)
      updateProperty({ variables: { resource: "myTime", key: "changeWorkingTimeEnabled", value: String(workingEnabled) } });
    if (showBalanceFrameEnabled !== initial.showBalanceFrame)
      updateProperty({ variables: { resource: "myTime", key: "showBalanceFrameEnabled", value: String(showBalanceFrameEnabled) } });
    if (approveAllEnabled !== initial.approveAll)
      updateProperty({ variables: { resource: "myTime", key: "approveAllRequestsEnabled", value: String(approveAllEnabled) } });
    if (examinerApprovalToSave !== initial.examinerApproval)
      updateProperty({ variables: { resource: "myTime", key: "examinerApprovalEnabled", value: String(examinerApprovalToSave) } });

    setInitial({
      change: changeEnabled,
      approval: approvalEnabled,
      working: workingEnabled,
      showBalanceFrame: showBalanceFrameEnabled,
      approveAll: approveAllEnabled,
      examinerApproval: examinerApprovalToSave,
    });

    setShowConfirm(false);
  };

  const handleCancel = () => {
    setChangeEnabled(initial.change);
    setApprovalEnabled(initial.approval);
    setWorkingEnabled(initial.working);
    setShowBalanceFrameEnabled(initial.showBalanceFrame);
    setApproveAllEnabled(initial.approveAll);
    setExaminerApprovalEnabled(initial.examinerApproval);
  };

  const allLoading =
    loadingChange || loadingApproval || loadingWorking || loadingShowBalanceFrame || loadingApprove || loadingExaminerApproval || saving;

  const hasChanges =
    changeEnabled !== initial.change ||
    approvalEnabled !== initial.approval ||
    workingEnabled !== initial.working ||
    showBalanceFrameEnabled !== initial.showBalanceFrame ||
    approveAllEnabled !== initial.approveAll ||
    examinerApprovalEnabled !== initial.examinerApproval;

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirm}
        onClose={handleSave}
        onCancel={() => setShowConfirm(false)}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />

      <OcCard label={t("OC_GENERAL_SETTINGS")} loading={allLoading}>
        <Box height="20px" />
        <OcSettingSwitch
          id="changeMyTimeEnabled"
          label={t("MY_TIME_CHANGE_ALLOWED")}
          info={t("MY_TIME_CHANGE_ALLOWED_INFO")}
          value={changeEnabled}
          onChange={(e) => setChangeEnabled(e.target.checked)}
          disabled={!enabledRequests || allLoading}
          isMobile={isMobile}
        />
        <Box height="36px" />
        <OcSettingSwitch
          id="changeWorkingTimeEnabled"
          label={t("MY_TIME_CHANGE_WORKING_TIME_ALLOWED")}
          info={t("MY_TIME_CHANGE_WORKING_TIME_ALLOWED_INFO")}
          value={workingEnabled}
          onChange={(e) => setWorkingEnabled(e.target.checked)}
          disabled={allLoading}
          isMobile={isMobile}
        />
        <Box height="36px" />
        <OcSettingSwitch
          id="showBalanceFrameEnabled"
          label={t("MY_TIME_SHOW_BALANCE_FRAME_ALLOWED")}
          info={t("MY_TIME_SHOW_BALANCE_FRAME_ALLOWED_INFO")}
          value={showBalanceFrameEnabled}
          onChange={(e) => setShowBalanceFrameEnabled(e.target.checked)}
          disabled={allLoading}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Collapse in={changeEnabled && enabledRequests}>
          <OcSettingsDivider />
          <Box height="8px" />
          <OcSettingSwitch
            id="approveAllRequestsEnabled"
            label={t("MY_TIME_APPROVE_ALL_REQUESTS_ALLOWED")}
            info={t("MY_TIME_APPROVE_ALL_REQUESTS_ALLOWED_INFO")}
            value={approveAllEnabled}
            onChange={(e) => setApproveAllEnabled(e.target.checked)}
            disabled={!changeEnabled || allLoading}
            isMobile={isMobile}
          />
          <Box height="20px" />
        </Collapse>
        <OcSettingsDivider />
        <Box height="8px" />
        <OcSettingSwitch
          id="userApprovalEnabled"
          label={t("MY_TIME_APPROVAL_ALLOWED")}
          info={t("MY_TIME_APPROVAL_ALLOWED_INFO")}
          value={approvalEnabled}
          onChange={(e) => setApprovalEnabled(e.target.checked)}
          disabled={!enabledRequests || allLoading}
          isMobile={isMobile}
        />
        <Box height="32px" />
        <OcSettingSwitch
          id="examinerApprovalEnabled"
          label={t("MY_TIME_APPROVAL_ALLOWED_BY_THE_EXAMINER")}
          info={t("MY_TIME_APPROVAL_ALLOWED_BY_THE_EXAMINER_INFO")}
          value={examinerApprovalEnabled}
          onChange={(e) => setExaminerApprovalEnabled(e.target.checked)}
          disabled={!enabledRequests || allLoading}
          isMobile={isMobile}
        />
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonText={t("OC_CANCEL")}
          rightButtonColor="secondary"
          handlePrimaryButton={() => setShowConfirm(true)}
          primaryButtonText={t("OC_SAVE")}
          primaryButtonDisabled={!hasChanges || allLoading}
        />
      </OcCard>
    </>
  );
};

export default MyTimeGeneralSettingsCard;
